/**
 * iOS / Web · detached input + focus 轮询选图
 *
 * 流程：
 * 1. 动态创建 hidden `<input type="file">` 挂到 body
 * 2. 监听 `change`（选中）、`cancel`（部分 WebView）、`focus`（取消或延迟交付）
 * 3. `clickHiddenFileInput`：iOS 先 `prepareFileInput` 再 click
 * 4. focus 回调在 `cancelPollMs` 后读 `input.files` 判定结果
 *
 * 注意：本模块 **不** 使用 `runPortfolioFilePickSession`。
 * session 的 focus 监听若在 click 前挂载，iOS 会误判取消。
 *
 * PDF on iOS 使用更长的 `PICK_CANCEL_POLL_MS_IOS_DOCUMENT`（文档选择器延迟填充 files）。
 */
import { clickHiddenFileInput } from "@/src/lib/bridge/prepareFileInput";
import { getBridge } from "@/src/lib/bridge/bridge";
import type { PortfolioUploadType } from "@components/PortfolioScreenshotModal/types";
import { resolvePortfolioPickConfig } from "./portfolioPickConfig";
import { resolvePortfolioInAppPlatformFlags } from "./portfolioInAppPlatform";

type PickedFilesMapper = (files: File[]) => Promise<File[] | null>;

const passThroughPickedFiles: PickedFilesMapper = async (files) =>
  files.length > 0 ? files : null;

/**
 * @param uploadType image | pdf
 * @param mapPickedFiles 可选：对 `input.files` 做 stabilize 等后处理；默认非空即返回
 */
export async function pickPortfolioFilesWithFocusFallback(
  uploadType: PortfolioUploadType,
  mapPickedFiles: PickedFilesMapper = passThroughPickedFiles,
): Promise<File[] | null> {
  if (typeof document === "undefined") {
    return null;
  }

  const bridge = getBridge();
  const { preset, accept, multiple, cancelPollMs } = resolvePortfolioPickConfig(
    uploadType,
    resolvePortfolioInAppPlatformFlags(),
  );

  return new Promise<File[] | null>((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.multiple = multiple;
    input.style.display = "none";
    document.body.appendChild(input);

    let settled = false;

    const cleanup = () => {
      window.removeEventListener("focus", onWindowFocus);
      input.removeEventListener("change", onChange);
      input.removeEventListener("cancel", onCancel);
      input.remove();
    };

    const finish = (files: File[] | null) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(files);
    };

    const finishFromInput = () => {
      void (async () => {
        try {
          const picked = Array.from(input.files || []);
          finish(await mapPickedFiles(picked));
        } catch {
          finish(null);
        }
      })();
    };

    const onChange = () => {
      finishFromInput();
    };

    const onCancel = () => {
      finish(null);
    };

    /** 用户取消或 iOS 文档选择器延迟交付时，focus 回来后轮询 files */
    const onWindowFocus = () => {
      window.setTimeout(() => {
        if (settled) {
          return;
        }
        finishFromInput();
      }, cancelPollMs);
    };

    input.addEventListener("change", onChange);
    input.addEventListener("cancel", onCancel);
    window.addEventListener("focus", onWindowFocus);

    void clickHiddenFileInput(bridge, { current: input }, preset).catch(() => {
      finish(null);
    });
  });
}
